import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Shield, Sparkles, Flame, Droplets } from 'lucide-react';
import { optimizeImage, generateSrcSet } from '../utils';
import { SITE_URL } from '../data/Constants';
import SEO from '../components/SEO';
import Schema from '../components/Schema';

const NapoleonLandingPage = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: <Flame className="w-8 h-8 text-primary" />,
      title: "Masterful Flamelifting",
      description: "Industry-leading flame technology that creates ultra-realistic, mesmerizing fire displays."
    },
    {
      icon: <Sparkles className="w-8 h-8 text-primary" />,
      title: "Premium Aesthetics",
      description: "Clean lines, expansive glass, and high-end finishes designed for modern luxury."
    },
    {
      icon: <Shield className="w-8 h-8 text-primary" />,
      title: "President's Limited Lifetime Warranty",
      description: "Peace of mind with one of the most comprehensive warranties in the industry."
    },
    {
      icon: <Droplets className="w-8 h-8 text-primary" />,
      title: "Efficient Heating",
      description: "Advanced engineering maximizes heat output while minimizing energy consumption."
    }
  ];

  const categories = [
    {
      title: "Gas Fireplaces",
      image: "https://assets.mainlandfireplaces.com/content/napoleon/ascent-multi-view/BHD4PGN-Web-Gallery-03-5D3_0792-Edit__1_.avif",
      description: "Timeless warmth and powerful performance.",
      filter: "Gas",
      position: "center"
    },
    {
      title: "Electric Fireplaces",
      image: "https://assets.mainlandfireplaces.com/content/napoleon/luminex/NEFL65LCD-3SV-Web-Gallery-09-Original_JPG-Luminex-NEFL65LCD-3SV-Life-3-sided-birch-blackcrystals-logs.avif",
      description: "Plug-and-play ambiance for any room.",
      filter: "Electric",
      position: "center 70%" // Pull the fireplace up
    },
    {
      title: "Outdoor Grills",
      image: "https://assets.mainlandfireplaces.com/content/napoleon/travelq-pro285/PRO285-BK-Web-Gallery-07-PRO285-lifestyle9.avif",
      description: "Elevate your backyard culinary experience.",
      filter: "Outdoor Kitchen",
      position: "right 60%" // Focus on the grill
    }
  ];


  return (
    <div className="bg-white min-h-screen pt-[72px] font-sans selection:bg-primary selection:text-white">
      <SEO
        title="Napoleon Fireplaces & Grills | Premium Dealer"
        description="Discover the luxury and performance of Napoleon fireplaces and grills at Mainland Fireplaces. Authorized dealer based in Langley, BC, serving the Lower Mainland, Interior, Island, and Northern BC."
        keywords="Napoleon fireplaces, Napoleon grills, gas fireplaces, electric fireplaces, outdoor kitchens, Langley, Vancouver, Fraser Valley"
        canonical={`${SITE_URL}/napoleon`}
      />
      <Schema type="LocalBusiness" />
      <Schema
        type="BreadcrumbList"
        data={{
          items: [
            { name: 'Home', path: '/' },
            { name: 'Napoleon', path: '/napoleon' }
          ]
        }}
      />
      <Schema
        type="CollectionPage"
        data={{
          name: "Napoleon Fireplaces & Grills | Premium Dealer",
          description: "Discover the luxury and performance of Napoleon fireplaces and grills at Mainland Fireplaces. Authorized dealer based in Langley, BC.",
          url: `${SITE_URL}/napoleon`
        }}
      />

      {/* HERO SECTION - HIGH CONVERSION DESIGN */}
      <section className="relative h-[85vh] min-h-[600px] flex items-center justify-center overflow-hidden bg-gray-900 isolate">
        <div className="absolute inset-0 z-0">
          <img
            src={optimizeImage("https://assets.mainlandfireplaces.com/content/napoleon/trivista-primis/NEFB60H-3SV-Web-Gallery-03-Trivista-NEFB60H-3SV_2-gallery-1600px.avif", 1600)}
            srcSet={generateSrcSet("https://assets.mainlandfireplaces.com/content/napoleon/trivista-primis/NEFB60H-3SV-Web-Gallery-03-Trivista-NEFB60H-3SV_2-gallery-1600px.avif")}
            sizes="100vw"
            alt="Napoleon Trivista Primis 3-sided electric fireplace integrated into a luxury living room wall"
            width={1600}
            height={900}
            className="w-full h-full object-cover opacity-60 mix-blend-overlay"
            fetchPriority="high"
          />
          {/* Advanced Gradient Overlay for premium feel */}
          <div className="absolute inset-0 bg-linear-to-r from-black/90 via-black/60 to-transparent"></div>
          <div className="absolute inset-0 bg-linear-to-t from-gray-900 via-transparent to-transparent opacity-80"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-12">

          {/* Main CTA Block */}
          <div className={`max-w-2xl transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}>
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-8">
              <span className="w-2 h-2 rounded-full bg-[#FFD700] animate-pulse"></span>
              <span className="text-white text-xs font-bold uppercase tracking-[0.2em]">Napoleon Authorized Gold Dealer</span>
            </div>

            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl text-white uppercase leading-[0.9] mb-6 drop-shadow-2xl">
              Celebrating <br />
              <span className="text-transparent bg-clip-text bg-linear-to-r from-primary to-[#ff9d5c]">50 Years of Innovation</span>
            </h1>

            <p className="text-gray-300 text-lg md:text-xl font-light mb-10 max-w-xl leading-relaxed border-l-2 border-primary pl-6">
              Experience the pinnacle of home comfort and outdoor living with Napoleon's award-winning engineering and breathtaking design.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/products?brand=Napoleon')}
                className="group relative px-8 py-4 bg-primary text-white overflow-hidden rounded-sm text-sm font-bold uppercase tracking-widest transition-all hover:scale-105 hover:shadow-[0_0_40px_-10px_rgba(235,94,40,1)]"
              >
                <div className="absolute inset-0 w-full h-full bg-linear-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
                <span className="relative flex items-center justify-center gap-2">
                  Shop Napoleon <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </span>
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-4 bg-transparent border border-white/30 text-white rounded-sm text-sm font-bold uppercase tracking-widest hover:bg-white/10 hover:border-white transition-all"
              >
                Request Consultation
              </button>
            </div>
          </div>

          {/* Brand Logo Only */}
          <div className={`hidden lg:block relative transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-x-12 -translate-y-16 opacity-100' : 'translate-x-24 opacity-0'}`}>
            <img
              src={`${import.meta.env.BASE_URL}images/brands/napoleon.svg`}
              alt="Napoleon Logo"
              width={480}
              height={120}
              className="w-[480px] h-auto drop-shadow-2xl"
            />
          </div>
        </div>

        {/* 50th Anniversary Badge */}
        <div className={`absolute top-4 right-4 md:top-auto md:bottom-8 md:right-8 z-20 transition-all duration-1000 delay-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="group relative">
            <div className="absolute -inset-2 bg-primary/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <img
              src={`${import.meta.env.BASE_URL}images/brands/50-years.png`}
              alt="Napoleon 50 Years of Innovation"
              width={128}
              height={128}
              className="relative w-14 md:w-32 h-auto drop-shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF BAR */}
      <section className="bg-secondary py-6 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-center md:justify-between items-center gap-8 text-white/70">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            <span className="text-sm font-bold uppercase tracking-widest">50 Years Heritage</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            <span className="text-sm font-bold uppercase tracking-widest">Award Winning Design</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-primary" aria-hidden="true" />
            <span className="text-sm font-bold uppercase tracking-widest">Exceptional Warranties</span>
          </div>
        </div>
      </section>

      {/* WHY NAPOLEON SECTION (PREMIUM GRID) */}
      <section className="py-32 px-6 bg-white relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 max-w-3xl mx-auto">
            <h2 className="font-heading text-4xl md:text-6xl uppercase text-secondary mb-6">
              The <span className="text-primary italic">Napoleon</span> Difference
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              When you choose Napoleon, you're investing in a legacy of quality. Every fireplace and grill is engineered with meticulous attention to detail, ensuring long-lasting performance and stunning aesthetics.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="group p-8 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 border border-gray-100 hover:border-primary/20">
                <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  {feature.icon}
                </div>
                <h3 className="font-heading text-xl uppercase text-secondary mb-4 group-hover:text-primary transition-colors">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES SECTION (DYNAMIC HOVER REVEALS) */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 mb-16 flex flex-col md:flex-row justify-between items-end gap-6">
          <div>
            <h2 className="font-heading text-4xl md:text-6xl uppercase mb-4">Explore the <br /><span className="text-primary">Collection</span></h2>
          </div>
          <button
            onClick={() => navigate('/products?brand=Napoleon')}
            className="group flex items-center gap-3 text-sm font-bold uppercase tracking-widest text-white hover:text-primary transition-colors"
          >
            View Full Catalog <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
          </button>
        </div>

        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[600px]">
            {categories.map((cat, i) => (
              <div
                key={i}
                onClick={() => navigate(`/products?brand=Napoleon&category=${cat.filter}`)}
                className="relative group cursor-pointer rounded-xl overflow-hidden shadow-2xl isolate"
              >
                <img
                  src={optimizeImage(cat.image, 800)}
                  alt={`Explore Napoleon ${cat.title} series`}
                  width={800}
                  height={600}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                  style={{ objectPosition: cat.position }}
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/90 via-black/40 to-transparent group-hover:bg-black/40 transition-colors duration-500"></div>

                <div className="absolute inset-x-0 bottom-0 p-8 flex flex-col justify-end transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500">
                  <div className="w-12 h-1 bg-primary mb-6 transform scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 delay-100"></div>
                  <h3 className="font-heading text-3xl uppercase text-white mb-2">{cat.title}</h3>
                  <p className="text-gray-300 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200 mb-6">
                    {cat.description}
                  </p>
                  <span className="inline-flex items-center gap-2 text-primary font-bold uppercase tracking-widest text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-300">
                    Shop Category <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERT INSTALLATION CTA */}
      <section className="py-32 px-6 relative overflow-hidden bg-white">
        <div className="absolute inset-y-0 right-0 w-1/3 bg-gray-50 pointer-events-none hidden lg:block"></div>
        <div className="max-w-7xl mx-auto relative z-10 flex flex-col lg:flex-row items-center gap-16">
          <div className="flex-1 space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-gray-100 rounded-sm">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-gray-600 text-xs font-bold uppercase tracking-widest">Certified Installation</span>
            </div>
            <h2 className="font-heading text-4xl md:text-6xl uppercase text-secondary leading-tight">
              Precision Sales.<br /> <span className="text-primary italic">Expert Installation.</span>
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed max-w-xl">
              Mainland Fireplaces provides fully certified, professional installation for all Napoleon products. From pulling permits to the final finish work, our in-house team ensures a flawless integration into your home.
            </p>
            <div className="pt-4">
              <ul className="space-y-4 mb-8">
                {['WETT Certified Technicians', 'Gas Contractor License', 'Complete Project Management'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-secondary font-bold uppercase text-sm tracking-wide">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-4 bg-secondary text-white uppercase font-bold tracking-widest text-sm rounded-sm hover:bg-primary transition-colors shadow-lg"
              >
                Schedule Consultation
              </button>
            </div>
          </div>

          <div className="flex-1 relative">
            <div className="absolute inset-0 bg-primary translate-x-4 translate-y-4 rounded-2xl"></div>
            <img
              src={`${import.meta.env.BASE_URL}images/heros/gas.avif`}
              alt="Mainland Fireplaces expert technician team performing a Napoleon installation"
              width={800}
              height={500}
              loading="lazy"
              className="relative z-10 w-full rounded-2xl shadow-2xl object-cover h-[500px]"
            />
          </div>
        </div>
      </section>

    </div>
  );
};

export default NapoleonLandingPage;
